import React, { useState, useEffect } from "react";
import { Modal, Input, Button, Radio, Typography, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  createFundingIntent,
  initializeFundingPayment,
} from "../../redux/Actions/walletAction";
import { getWalletReducer } from "../../redux/Reducer/walletReducer";
import { SafetyCertificateOutlined } from "@ant-design/icons";

// Import your OPay logo (renamed to match your snippet)
import opayLogo from "../../asset/opayimg.png";

const { Text } = Typography;

const FundWalletModal = ({ open, onCancel, walletId }) => {
  const [amount, setAmount] = useState("");
  const [gateway, setGateway] = useState("paystack"); // Default
  const dispatch = useDispatch();
  const { isCreatingIntent, fundingIntent, isInitializingPayment } =
    useSelector(getWalletReducer);

  // 1. Submit Amount -> Create Intent
  const handleProceed = () => {
    if (!amount || amount < 10) return;
    dispatch(
      createFundingIntent({
        wallet_id: walletId,
        amount: parseFloat(amount),
      }),
    );
  };

  // 2. Watch for Intent Success -> Initialize Payment
  // 2. Watch for Intent Success -> Initialize Payment
  // 2. Watch for Intent Success -> Initialize Payment
  useEffect(() => {
    if (fundingIntent?.status === true && fundingIntent?.intent?.uuid) {
      // 1. Build the exact verification URL (e.g., http://localhost:3000/verify?gateway=opay)
      const exactRedirectUrl = `${window.location.origin}/verify?gateway=${gateway}`;

      // 2. (Optional but Safe Fallback) Save to local storage just in case the payment
      // gateway decides to strip query parameters during the redirect.
      localStorage.setItem("funding_gateway", gateway);

      dispatch(
        initializeFundingPayment({
          intent_id: fundingIntent.intent.uuid,
          gateway: gateway,
          redirect_url: exactRedirectUrl, // Send the exact URL
        }),
      );
    }
  }, [fundingIntent, dispatch, gateway]);

  const loading = isCreatingIntent || isInitializingPayment;

  // Custom Gateway Card Component
  const GatewayOption = ({ value, label, logo, color }) => (
    <div
      onClick={() => !loading && setGateway(value)}
      style={{
        border: gateway === value ? `2px solid #252A4D` : "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "10px",
        cursor: loading ? "not-allowed" : "pointer",
        backgroundColor: gateway === value ? "#F4F6F8" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
        transition: "all 0.3s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {logo ? (
          <img
            src={logo}
            alt={label}
            style={{ height: "25px", objectFit: "contain" }}
          />
        ) : (
          <SafetyCertificateOutlined
            style={{ fontSize: "20px", color: color }}
          />
        )}
        <span style={{ fontWeight: 500, color: "#333" }}>{label}</span>
      </div>
      <Radio checked={gateway === value} />
    </div>
  );

  return (
    <Modal
      title={
        <div
          style={{ textAlign: "center", fontWeight: "bold", fontSize: "18px" }}
        >
          Fund Wallet
        </div>
      }
      open={open}
      onCancel={!loading ? onCancel : null}
      footer={null}
      centered
      width={400}
      maskClosable={!loading}
    >
      <div className="pt-2">
        <Text type="secondary" style={{ fontSize: "13px" }}>
          How much would you like to add?
        </Text>
        <div style={{ marginTop: "5px", marginBottom: "20px" }}>
          <Input
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            prefix={
              <span style={{ fontWeight: "bold", color: "#333" }}>₦</span>
            }
            size="large"
            style={{ fontSize: "20px", fontWeight: "bold", padding: "10px" }}
            disabled={loading}
          />
          {amount && amount < 10 && (
            <div style={{ color: "red", fontSize: "11px", marginTop: "5px" }}>
              Minimum amount is ₦10
            </div>
          )}
        </div>

        <Text type="secondary" style={{ fontSize: "13px" }}>
          Select Payment Method
        </Text>
        <div style={{ marginTop: "10px" }}>
          <GatewayOption
            value="opay"
            label="OPay Wallet / Card"
            logo={opayLogo}
            color="#20c997"
          />
          <GatewayOption
            value="paystack"
            label="Paystack"
            logo="https://pluginicons.craft-cdn.com/commerce-paystackB9kA1pnH1vXzDH6GVLhRwcFRgcBVfIrmICuD.svg?1592998530"
            color="#09a5db"
          />
          <GatewayOption
            value="flutterwave"
            label="Flutterwave"
            logo="https://logosmarcas.net/wp-content/uploads/2022/06/Flutterwave-Simbolo.png"
            color="#f5a623"
          />
        </div>

        <Divider style={{ margin: "15px 0" }} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "15px",
          }}
        >
          <Text type="secondary">Total to pay:</Text>
          <Text strong style={{ fontSize: "16px" }}>
            {amount ? `₦${new Intl.NumberFormat().format(amount)}` : "₦0.00"}
          </Text>
        </div>

        <Button
          type="primary"
          block
          size="large"
          style={{
            backgroundColor: "#252A4D",
            height: "45px",
            fontSize: "16px",
            fontWeight: "500",
          }}
          onClick={handleProceed}
          loading={loading}
          disabled={!amount || amount < 10}
        >
          {loading ? "Initializing Payment..." : "Proceed to Payment"}
        </Button>

        <div style={{ textAlign: "center", marginTop: "15px" }}>
          <Text type="secondary" style={{ fontSize: "11px" }}>
            <SafetyCertificateOutlined /> Secured by{" "}
            {gateway.charAt(0).toUpperCase() + gateway.slice(1)}
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default FundWalletModal;
